import { Button, ButtonProps, InputNumber, InputNumberProps } from 'antd';
import React, { useState, useEffect, ReactNode, KeyboardEvent } from 'react';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import clsx from 'clsx';

interface WalletInputNumberProps extends Omit<InputNumberProps<number>, 'onChange' | 'value'> {
  value?: number;
  onChange?: (value: number) => void;
  step?: number;
  limit: number;
  max?: number;
  error?: boolean;
  hasNotificationToggleAccessByRole?: boolean;
}
interface IconButtonProps extends ButtonProps {
  icon: ReactNode;
}

const IconButton = ({ icon, onClick, ...rest }: IconButtonProps) => (
  <Button icon={icon} shape="circle" onClick={onClick} type="text" className="!size-7 !min-w-0" {...rest} />
);
const WalletInputNumber: React.FC<WalletInputNumberProps> = ({
  value = 0,
  onChange,
  step = 1,
  limit,
  max,
  error = false,
  ...inputrest
}) => {
  const [internalValue, setInternalValue] = useState<number>(value);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const updateValue = (val: number) => {
    setInternalValue(val);
    onChange?.(val);
  };

  const handleChange = (newValue: number | null) => {
    if (newValue == null) return;
    updateValue(newValue);
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    // Allow keys like Backspace, Delete, Tab, Arrow keys
    if (event.ctrlKey || event.metaKey || event.key.length > 1) {
      return;
    }

    const allowedCharacters = /^[0-9]$/;
    if (!allowedCharacters.test(event.key)) {
      event.preventDefault();
    }

    // disable 0 in the first position
    const inputElement = event.target as HTMLInputElement;
    if (event.key === '0' && inputElement.selectionStart === 0) {
      event.preventDefault();
    }
  };

  const inputFormatter = (value: number | undefined) => {
    if (value == null || !value) return '0.00';
    const num = typeof value === 'number' ? value : parseFloat(value);
    return `${num.toFixed(2)}`;
  };

  return (
    <div
      className={clsx(
        'px-2 py-1 rounded-lg border flex justify-between items-center',
        error ? 'border-red-500' : 'border-gray-500'
      )}
    >
      <IconButton
        icon={<MinusOutlined />}
        onClick={() => updateValue(Math.max(internalValue - step, limit))}
        disabled={internalValue <= limit}
      />
      <div className="!text-center !w-25">
        <InputNumber
          value={value}
          min={0}
          step={step}
          prefix="$"
          controls={false}
          onChange={handleChange}
          formatter={inputFormatter}
          onKeyDown={handleKeyPress}
          className="!w-full !border-none !shadow-none"
          {...inputrest}
        />
      </div>
      <IconButton
        icon={<PlusOutlined />}
        onClick={() => updateValue(internalValue + step)}
        disabled={max !== undefined && internalValue >= max}
      />
    </div>
  );
};

export { WalletInputNumber };
